import { Link } from "react-router-dom";
import { Instagram, ExternalLink } from "lucide-react";
import { RedThread } from "@/components/latir/Graphics";
import { useLang } from "@/context/LanguageContext";

// External URLs — real ones configured. Fundarosa links left null until confirmed.
const IG_ARTECONSCIENCIA = "https://www.instagram.com/arteconcienciasv/";
const IG_FUNDAROSA = "https://www.instagram.com/asociacionfundarosa?igsh=MXc2d2pwaGh1N210NA==";
const WEB_FUNDAROSA = "https://fundarosa.com";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const navLinks = [
    { to: "/", label: t("nav.home"), testid: "footer-link-home" },
    { to: "/latir", label: t("nav.about"), testid: "footer-link-sobre-latir" },
    { to: "/artistas", label: t("nav.artists"), testid: "footer-link-artistas" },
    { to: "/programa", label: t("nav.programa"), testid: "footer-link-agenda" },
    { to: "/reservas", label: t("nav.visitas"), testid: "footer-link-reservas" },
    { to: "/sobre-arteconcienciasv", label: t("nav.sobreac"), testid: "footer-link-sobre-ac" },
    { to: "/prensa", label: t("nav.prensa"), testid: "footer-link-prensa" },
  ];

  const connectLinks = [
    {
      href: IG_ARTECONSCIENCIA,
      label: "Instagram · Arteconciencia",
      aria: "Instagram de Arteconciencia",
      icon: Instagram,
      testid: "footer-ig-arteconsciencia",
    },
    {
      href: IG_FUNDAROSA,
      label: "Instagram · Fundarosa",
      aria: "Instagram de Fundarosa",
      icon: Instagram,
      testid: "footer-ig-fundarosa",
    },
    {
      href: WEB_FUNDAROSA,
      label: "Web · Fundarosa",
      aria: "Sitio web de Fundarosa",
      icon: ExternalLink,
      testid: "footer-web-fundarosa",
    },
  ];

  return (
    <footer data-testid="footer" className="bg-paper-deep">
      {/* Nivel 1 — Aliados (compact) */}
      <div
        className="border-t border-line px-2 sm:px-4 md:px-6 lg:px-4 pt-2 md:pt-3 pb-1 md:pb-2"
        data-testid="sponsors"
      >
        <p className="text-center label-ui mb-1 md:mb-2">{t("footer.sponsors")}</p>
        <img
          src="/brand/patrocinadores.webp"
          alt={t("footer.sponsors")}
          data-testid="sponsors-logos"
          className="mx-auto w-full max-w-[980px] h-auto object-contain"
          style={{ imageRendering: "auto" }}
        />
      </div>

      {/* Divisor — trazo rojo curatorial */}
      <div className="relative h-10 overflow-hidden opacity-50" aria-hidden>
        <RedThread className="w-full h-20 -translate-y-4" strokeWidth={1} />
      </div>

      {/* Nivel Principal — 3 columnas */}
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-start">
          {/* Columna 1 — LATIR / Identidad (45% ≈ 5/12) */}
          <div className="md:col-span-5" data-testid="footer-identity">
            <img
              src="/brand/latir-logo-official.webp"
              alt="LATIR"
              className="w-[128px] h-auto"
            />
            <p className="font-ui font-light uppercase tracking-[0.34em] text-ink-mute mt-3 text-[0.62rem]">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 space-y-2">
              <p className="font-editorial italic text-[0.92rem] text-ink-soft leading-snug">
                {t("footer.by.prefix")}
                <span className="not-italic font-ui uppercase tracking-[0.14em] text-ink text-[0.78rem]">
                  {t("footer.by.entity")}
                </span>
              </p>
              <p className="font-editorial italic text-[0.86rem] text-ink-soft/90 leading-snug">
                {t("footer.support.prefix")}
                <span className="not-italic font-ui uppercase tracking-[0.14em] text-ink text-[0.72rem]">
                  {t("footer.support.entity")}
                </span>
              </p>
              <p className="label-ui text-ink-mute pt-1">{t("footer.venue")}</p>
            </div>
          </div>

          {/* Columna 2 — Navegación (25% ≈ 3/12) */}
          <div className="md:col-span-3" data-testid="footer-nav">
            <p className="label-ui mb-4 text-latir-red">{t("footer.nav")}</p>
            <ul className="space-y-2.5">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="font-ui text-[0.82rem] tracking-[0.04em] text-ink hover:text-latir-red transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Columna 3 — Conecta (30% ≈ 4/12) */}
          <div className="md:col-span-4" data-testid="footer-connect">
            <p className="label-ui mb-4 text-latir-red">{t("footer.connect")}</p>
            <ul className="space-y-2.5">
              {connectLinks.map((c) => {
                const Icon = c.icon;
                const isDisabled = !c.href;
                return (
                  <li key={c.label}>
                    {isDisabled ? (
                      <span
                        data-testid={`${c.testid}-pending`}
                        className="font-ui text-[0.82rem] tracking-[0.04em] text-ink-mute/60 inline-flex items-center gap-2 cursor-default"
                        title="URL pendiente de confirmación"
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.4} />
                        {c.label}
                      </span>
                    ) : (
                      <a
                        href={c.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={c.aria || c.label}
                        data-testid={c.testid}
                        className="font-ui text-[0.82rem] tracking-[0.04em] text-ink hover:text-latir-red transition-colors duration-300 inline-flex items-center gap-2"
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.4} />
                        {c.label}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Divisor fino */}
        <div className="hairline mt-10 mb-5" />

        {/* Bottom — copyright */}
        <div className="flex items-center justify-between">
          <p
            className="font-ui text-[0.7rem] tracking-[0.08em] text-ink-mute uppercase"
            data-testid="footer-copyright"
          >
            © {year} LATIR
          </p>
        </div>
      </div>
    </footer>
  );
}
